import type { SpanPayload, TransactionPayload } from './types.js';

function uuid(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/** Shorter id than a full UUID - span_id only needs to be unique within one trace. */
function shortId(): string {
  return uuid().replace(/-/g, '').slice(0, 16);
}

/** One timed unit of work inside a transaction (a DB query, an outbound call). */
export class BugCatchSpan {
  readonly spanId = shortId();
  readonly startedAt = Date.now();
  durationMs: number | undefined;
  status = 'ok';

  constructor(
    private readonly transaction: BugCatchTransaction,
    public readonly parentSpanId: string | undefined,
    public readonly op: string,
    public description?: string,
    public tags?: Record<string, string>,
  ) {}

  /** Start a span nested inside this one. */
  startChild(op: string, description?: string, tags?: Record<string, string>): BugCatchSpan {
    return this.transaction.startChildSpan(this.spanId, op, description, tags);
  }

  setStatus(status: string): this {
    this.status = status;
    return this;
  }

  finish(status?: string): void {
    if (this.durationMs !== undefined) return; // already finished
    if (status) this.status = status;
    this.durationMs = Date.now() - this.startedAt;
  }

  /** @internal */
  toPayload(): SpanPayload {
    return {
      span_id: this.spanId,
      parent_span_id: this.parentSpanId,
      op: this.op,
      description: this.description,
      start_timestamp: new Date(this.startedAt).toISOString(),
      duration_ms: this.durationMs ?? Date.now() - this.startedAt,
      status: this.status,
      tags: this.tags,
    };
  }
}

/**
 * The root of one traced operation - an HTTP request, a background job.
 * Created via `BugCatch.startTransaction()`, not directly.
 */
export class BugCatchTransaction {
  readonly traceId = uuid();
  readonly startedAt = Date.now();
  durationMs: number | undefined;
  status = 'ok';
  statusCode: number | undefined;
  tags: Record<string, string> | undefined;

  private readonly spans: BugCatchSpan[] = [];

  constructor(
    public readonly name: string,
    public readonly op: string,
    private readonly onFinish: (txn: BugCatchTransaction) => void,
  ) {}

  /** Start a span as a direct child of the transaction. */
  startChild(op: string, description?: string, tags?: Record<string, string>): BugCatchSpan {
    return this.startChildSpan(undefined, op, description, tags);
  }

  setStatus(status: string): this {
    this.status = status;
    return this;
  }

  /** Convenience: maps >=400 (client errors included, not just 5xx server errors) to 'error', everything else to 'ok'. */
  setHttpStatus(statusCode: number): this {
    this.statusCode = statusCode;
    this.status = statusCode >= 400 ? 'error' : 'ok';
    return this;
  }

  setTag(key: string, value: string): this {
    this.tags = { ...this.tags, [key]: value };
    return this;
  }

  finish(status?: string): void {
    if (this.durationMs !== undefined) return; // already finished
    if (status) this.status = status;
    this.durationMs = Date.now() - this.startedAt;

    // Finish any spans the caller forgot to close, so a bug in instrumentation
    // code doesn't silently drop the whole transaction.
    for (const span of this.spans) {
      span.finish();
    }

    this.onFinish(this);
  }

  /** @internal */
  startChildSpan(
    parentSpanId: string | undefined,
    op: string,
    description?: string,
    tags?: Record<string, string>,
  ): BugCatchSpan {
    const span = new BugCatchSpan(this, parentSpanId, op, description, tags);
    this.spans.push(span);
    return span;
  }

  /** @internal */
  toPayload(userId?: string, environment?: string, release?: string): TransactionPayload {
    return {
      trace_id: this.traceId,
      name: this.name,
      op: this.op,
      start_timestamp: new Date(this.startedAt).toISOString(),
      duration_ms: this.durationMs ?? Date.now() - this.startedAt,
      status: this.status,
      status_code: this.statusCode,
      environment,
      release,
      user_id: userId,
      tags: this.tags,
      spans: this.spans.map((s) => s.toPayload()),
    };
  }
}
